import type EventEmitter from 'eventemitter3';
import { assertBoolean } from '../validators';
import type { MixinBase, MergeConstructors, GetConstructorParameters } from '../types';

import type { ParentMixinSettings } from './parentMixin';
import type { SerializableMixinSettings } from './serializableMixin';

export type UpdateFlowMixinSettings = {
    /**
     * if the update loop should run automatically
     */
    autoUpdate?: boolean;
} & Partial<ParentMixinSettings> &
    Partial<SerializableMixinSettings>;

export type UpdateFlowMixinInterface = {
    new (): typeof UpdateFlowMixin;
    _classType: string;
    set requiresUpdate(value: boolean);
    get requiresUpdate(): boolean;
    get processing(): boolean;
    set autoUpdate(value: boolean);
    get autoUpdate(): boolean;
    set _autoUpdate(value: boolean);
    atomicUpdate(callback: () => void): void;
    _checkAndFixProperties(): void;
    _update(): void;
    _emitChanged(): void;
    _notifyAfterUpdate(): void;
    _fixAndUpdate(): void;
    canEmitChangedEvent: boolean;
} & Partial<EventEmitter>;

/**
 * This Mixin contains our *update* model. This pattern helps us to:
 * - keep all our components property values valid
 * - update all necessary components on a property change
 * - group updates together
 * - determine when an update is complete
 *
 * An *update* is triggered by setting `requiresUpdate` to true. When that happens, the update will run through
 * the following steps:
 * - **fix** any properties that need fixing by calling in `_checkAndFixProperties`
 * - **update** our internals by calling `_update`
 * - **notify** that the update is complete by calling `_notifyAfterUpdate`
 *
 * By default, `_notifyAfterUpdate` propagates the update to the parent (if a parent with `requiresUpdate` exists),
 * otherwise it calls `_emitChanged`. Override `_notifyAfterUpdate` to change this behaviour — for example,
 * classes whose parent should be redrawn rather than updated should call `parent._childRequestingRedraw(this)`.
 *
 * To make use of the *update* model, each child class property needs to use the following pattern:
 * ```javascript
 * this.#brightness = 1.0;
 *
 * get brightness() {
 *  return this.#brightness;
 * }
 *
 * set brightness(value) {
 *  if (this.brightness === value) {
 *      return;
 *  }
 *  this.#brightness = value;
 *  this.requiresUpdate = true;
 * }
 * ```
 * In this case, each time `brightness` changes, the class will update by calling our *update* model.
 *
 * To group *updates* together, use the following pattern:
 * ```javascript
 * const initAutoUpdate = this.autoUpdate;
 * this.autoUpdate = false;
 *
 * // set all properties here
 *
 * this.autoUpdate = initAutoUpdate
 * ```
 * @deprecated use TypedUpdateFlowMixin
 */
export const UpdateFlowMixin = <TBase extends MixinBase>(Base: TBase) => {
    const Derived = class extends (Base as any) {
        _classType = 'UpdateFlowMixin';

        _requiresUpdate = false;
        _autoUpdate = true;
        #processing = false;
        canEmitChangedEvent = true;

        constructor(settings: UpdateFlowMixinSettings = {}) {
            // eslint-disable-next-line constructor-super
            super(settings);

            if (settings.hasOwnProperty('autoUpdate')) {
                assertBoolean(settings.autoUpdate!);
                this._autoUpdate = settings.autoUpdate!;
            }
        }

        /**
         * Setting to true will trigger the update cycle. This should be set when the object has changes which need to be
         * processed.
         */
        set requiresUpdate(value: boolean) {
            this._requiresUpdate = value;
            if (value) {
                this._fixAndUpdate();
            }
        }

        get requiresUpdate(): boolean {
            return this._requiresUpdate;
        }

        /**
         * Determines whether or not the update loop is processing a change
         */
        get processing() {
            return this.#processing;
        }

        /**
         * If true, automatically updates any property changes. If false, doesn't process any property changes until
         * autoUpdate is set to true. Keeping autoUpdate set to false before changing several properties at once, allows us
         * to keep the processing until the end, hereby significantly increasing our system's performance.
         */
        set autoUpdate(value: boolean) {
            if (this.autoUpdate === value) {
                return;
            }
            this._autoUpdate = value;
            if (value === true) {
                this._fixAndUpdate();
            }
        }

        get autoUpdate() {
            return this._autoUpdate;
        }

        /**
         * Runs for the current state of the class, correcting or updating any properties as necessary. It's important that
         * this method does not throw errors, but rather fixes the properties in-line.
         */
        _checkAndFixProperties() {
            // stub to be overridden in child class
        }

        /**
         * The update logic stub. To be overridden by the child class. If anything needs to be updated, then this is the
         * place to do it.
         */
        _update() {
            // stub to be overridden in child class
        }

        /**
         * The change complete logic stub. To be overridden by the child class. If anything needs to informed, then this
         * is the place to do it.
         */
        _emitChanged() {
            if (this.canEmitChangedEvent) {
                this.emit('changed');
            }
        }

        /**
         * Called after an update completes to propagate the change. Default behaviour: if the parent has
         * `_childRequestingRedraw`, call it (visual-only rebuild); otherwise if the parent has `requiresUpdate`,
         * set it; otherwise emit `changed` directly. Override to change propagation — e.g. force `requiresUpdate`
         * on a parent that also has `_childRequestingRedraw` when a full update is needed.
         */
        _notifyAfterUpdate() {
            const parent = this.parent as Record<string, unknown> | null;
            if (parent && '_childRequestingRedraw' in parent) {
                (
                    parent as { _childRequestingRedraw: (child: unknown) => void }
                )._childRequestingRedraw(this);
                return;
            }
            if (parent && 'requiresUpdate' in parent) {
                (parent as { requiresUpdate: boolean }).requiresUpdate = true;
                return;
            }
            this._emitChanged();
        }

        /**
         * Perform a single part of an object's update according to update flow rules:
         *  - don't update for destroyed object
         *  - manage the internal processing flag properly
         *  - only update if autoUpdate is true
         *
         * Note: Generally, updates should happen through _fixAndUpdate, by setting requiresUpdate to true.
         * However, sometimes it's necessary to update a specific part of another object
         * without running the whole update loop, for performance reason.
         *
         * In these cases, it's safer to wrap the partial update in `objectToUpdate.atomicUpdate()`.
         */
        atomicUpdate(callback: () => void) {
            // no longer update if object is destroyed
            if (this.destroyed) {
                return;
            }

            // if we're already processing (aka updating) don't do it again – exit early!
            if (this.processing) {
                return;
            }

            // If we require update for any reason, we can skip this atomic update.
            // It will happen anyway later during the update loop.
            if (this.requiresUpdate) {
                return;
            }

            // set the #processing flag so we'll exit early above if we trigger another _fixAndUpdate() call while
            // calling `_update()`
            this.#processing = true;

            // return if we don't want to run an updates for the moment
            if (!this.autoUpdate) {
                this.#processing = false;
                return;
            }

            try {
                // update the component
                callback();
            } finally {
                this.#processing = false;
            }

            if (this.requiresUpdate) {
                // Something else happened while we set processing to true
                // But we did not perform a full update here, so
                // we should perform one now.
                this._fixAndUpdate();
            }
        }

        /**
         * Runs update flow logic.
         * Determines what, if anything, needs to be fixed and updated.
         */
        _fixAndUpdate() {
            // no longer update if object is destroyed
            if (this.destroyed) {
                return;
            }

            // if we're already processing (aka updating) don't do it again – exit early!
            if (this.processing) {
                return;
            }

            // set the #processing flag so we'll exit early above if we trigger another _fixAndUpdate() call while
            // calling `_update()`
            this.#processing = true;

            // verify our property values and correct if necessary
            this._checkAndFixProperties();

            // check if we have any changes to process
            if (!this.requiresUpdate) {
                this.#processing = false;
                return;
            }

            // return if we don't want to run an updates for the moment
            if (!this.autoUpdate) {
                this.#processing = false;
                return;
            }

            try {
                // update the component
                this._update();
            } finally {
                // the component has now been processed and thus no longer requires any update
                this.requiresUpdate = false;
                this.#processing = false;
            }

            this._notifyAfterUpdate();
        }
    };

    return Derived as unknown as MergeConstructors<
        new (settings?: UpdateFlowMixinSettings) => {
            // `extends (Base as any)` gives instances an implicit `[x: string]: any` index
            // signature; preserve it so the published surface stays compatible with web.
            [x: string]: any;
        } & {
            // Homomorphic mapped types render methods as function-valued properties, which
            // breaks subclasses that override them (TS2425). Exclude the overridable
            // methods from the mapped copy and re-declare them below with method syntax.
            [K in keyof InstanceType<typeof Derived> as K extends
                | 'atomicUpdate'
                | '_checkAndFixProperties'
                | '_update'
                | '_emitChanged'
                | '_notifyAfterUpdate'
                | '_fixAndUpdate'
                ? never
                : K]: InstanceType<typeof Derived>[K];
        } & {
            atomicUpdate(callback: () => void): void;
            _checkAndFixProperties(): void;
            _update(): void;
            _emitChanged(): void;
            _notifyAfterUpdate(): void;
            _fixAndUpdate(): void;
        },
        TBase
    >;
};

/**
 * Typed version of UpdateFlowMixin.
 */
export const TypedUpdateFlowMixin = <TBase>(Base: MixinBase<TBase>) => {
    const Derived = UpdateFlowMixin(Base as any);

    type ReturnType = UpdateFlowMixinInterface & TBase;

    return Derived as unknown as new (
        props: GetConstructorParameters<typeof Derived>,
    ) => ReturnType;
};
